import type { ReactNode, SVGProps } from "react";
import React, { useMemo, useState } from "react";
import { Link, router } from "expo-router";
import {
  Header as BaseHeader,
  Button,
  Exit,
  Logo,
  NavbarPopover as NavbarPopoverBase,
  ProfileIcon,
  Progress,
  SideBar,
  WarningSign,
  X,
} from "@ptsd/uikit";
import { ChevronDown } from "@tamagui/lucide-icons";
import { useQueryClient } from "@tanstack/react-query";
import type { ViewProps } from "tamagui";
import {
  Accordion,
  Anchor,
  Separator,
  Square,
  styled,
  Text,
  useMedia,
  View,
  XStack,
} from "tamagui";

import type { RouterInputs } from "~/utils/api";
import { api } from "~/utils/api";
import { logout } from "~/utils/logout";
import { BagReportForm } from "~/component/blocks/bag-report";
import { useMe, useMeOptional, usePathIncludes } from "~/hooks";
import { useFunnel } from "../feature";

const NAV_ITEMS = [
  { identifier: "", path: "/", name: "Головна", hidden: false },
  {
    identifier: "results",
    path: "/results/hmt",
    name: "Мої результати",
    hidden: true,
  },
  {
    identifier: "slices",
    path: "/slices",
    name: "Додаткова діагностика",
    hidden: true,
  },
];

type NavItem = (typeof NAV_ITEMS)[number];

export const Header: React.FC<
  {
    isTutorial?: boolean;
  } & ViewProps
> = ({ isTutorial = false, ...props }) => {
  const { sm } = useMedia();
  const person = useMeOptional();

  return (
    <BaseHeader.Body
      data-id={"header-body"}
      media={sm ? "md" : "lg"}
      userName={sm ? "Меню" : person?.name ?? "Меню"}
      renderButton={sm ? <SizeBarMenu /> : <NavbarPopover />}
      {...props}
    >
      {!sm && !isTutorial && <HeaderLinks />}
    </BaseHeader.Body>
  );
};

export const HeaderAuth = () => {
  const { sm } = useMedia();
  const media = sm ? "md" : "lg";
  return (
    <BaseHeader.Container media={media} backgroundColor={"transparent"}>
      <BaseHeader.Content media={media}>
        <Anchor href={"/"} unstyled={true}>
          <Logo size={media} />
        </Anchor>
      </BaseHeader.Content>
      {sm && (
        <BaseHeader.BottomLineContainer media={media}>
          <BaseHeader.BottomLine media={media} />
        </BaseHeader.BottomLineContainer>
      )}
    </BaseHeader.Container>
  );
};

const resolveHeaderLinks = (
  navItems: NavItem[],
  hasSlices: boolean,
  hasResults: boolean,
) =>
  navItems.map((item) => {
    if (item.identifier === "slices") {
      item.hidden = !hasSlices;
    }
    if (item.identifier === "results") {
      item.hidden = !hasResults;
    }
    return item;
  });

const HeaderLinks = () => {
  const { match } = usePathIncludes();
  const [task] = api.home.tasksStatus.useSuspenseQuery(undefined, {
    refetchOnMount: true,
  });

  const filteredNavItems = useMemo(
    () =>
      resolveHeaderLinks(
        NAV_ITEMS,
        task.todo.hasHooksResults,
        task.todo.hasResults,
      ).filter((item) => !item.hidden),
    [task],
  );

  return (
    <XStack alignItems="center" gap={"$4"} data-id={"navbar-links-container"}>
      {filteredNavItems.map((item) => (
        <Link href={item.path} key={item.path} data-id={"navbar-links"}>
          <BaseHeader.NavItem
            data-id={"navbar-items"}
            active={match(item.identifier)}
          >
            {item.name}
          </BaseHeader.NavItem>
        </Link>
      ))}
    </XStack>
  );
};

type QuestionnaireType = RouterInputs["survey"]["taskLookup"]["type"];

// Temporary solution for progress bar calculation in slices - assumes one questionnaire per slice
const resolveProgressBarValue = (
  isFinal: boolean,
  questionnaireProgress: number,
  questionnaireLength: number,
  currentStep: number,
  type: QuestionnaireType,
) => {
  if (isFinal) return 100;

  const stepBasedTypes = [
    "bat-12",
    "cips",
    "pss-10",
    "rse",
    "posttraumatic-growth",
    "dss-b",
    "asrs",
  ];

  if (stepBasedTypes.includes(type)) {
    const percentPerStep = 100 / questionnaireLength;
    return Math.floor(percentPerStep * currentStep);
  }

  return questionnaireProgress;
};

export const SurveyHeader: React.FC<{
  questionnaireProgress: number | null | undefined;
  questionnaireType: QuestionnaireType;
  isFinal: boolean;
}> = ({ isFinal, questionnaireProgress, questionnaireType }) => {
  const { sm } = useMedia();
  const { items, step } = useFunnel();

  const progressBarValue = useMemo(
    () =>
      resolveProgressBarValue(
        isFinal,
        questionnaireProgress ?? 0,
        items.length,
        step,
        questionnaireType,
      ),
    [isFinal, questionnaireProgress, items.length, step, questionnaireType],
  );

  const person = useMe();

  return (
    <BaseHeader.Body
      media={sm ? "md" : "lg"}
      data-id={"survey-header"}
      userName={person.name}
      renderButton={sm ? <SizeBarMenu /> : <NavbarPopover />}
      backgroundColor={"transparent"}
      maxWidth={1200}
      progressBar={
        <Progress
          data-id={"survey-progress-bar"}
          size={sm ? "md" : "lg"}
          value={progressBarValue}
          max={100}
          variant="default"
          rounded="lg"
        />
      }
    />
  );
};

type PopoverNavBarItems =
  | {
      icon: React.FC<SVGProps<SVGSVGElement>>;
      text: string;
      href: string;
      action: "redirect";
    }
  | {
      icon: React.FC<SVGProps<SVGSVGElement>>;
      text: string;
      action: "logout";
    }
  | {
      icon: React.FC<SVGProps<SVGSVGElement>>;
      text: string;
      action: "dialog";
    };

const POPOVER_NAV_BAR_ITEM = [
  {
    icon: WarningSign,
    text: "Помітили помилку?",
    action: "dialog",
  },

  {
    icon: ProfileIcon,
    text: "Профіль",
    href: "/profile",
    action: "redirect",
  },
  {
    icon: Exit,
    text: "Вихід",
    action: "logout",
  },
] satisfies PopoverNavBarItems[];

const NavbarPopover = () => {
  const [bagReportOpen, setBagReportOpen] = useState(false);

  const queryClient = useQueryClient();

  return (
    <>
      <BagReportForm
        open={bagReportOpen}
        setOpen={setBagReportOpen}
        closeDialog={() => setBagReportOpen(false)}
      />
      <NavbarPopoverBase.Body
        placement="bottom-end"
        data-id={"navbar-popover-body"}
      >
        <NavbarPopoverBase.Trigger
          cursor="pointer"
          data-id={"navbar-popover-trigger"}
        >
          <BaseHeader.HamburgerButton />
        </NavbarPopoverBase.Trigger>
        <NavbarPopoverBase.Content
          backgroundColor={"white"}
          gap={"$2"}
          data-id={"navbar-popover-content"}
        >
          {POPOVER_NAV_BAR_ITEM.map((item, index) => {
            const isLast = index === POPOVER_NAV_BAR_ITEM.length - 1;
            const Icon = item.icon;
            return (
              <NavbarPopoverBase.Close
                data-id={"navbar-popover-item-" + item.action}
                cursor="pointer"
                width={"100%"}
                alignItems="flex-start"
                onPress={() => {
                  if (item.action === "redirect") {
                    router.navigate(item.href);
                  }
                  if (item.action === "dialog") {
                    setBagReportOpen(true);
                  }
                  if (item.action === "logout") {
                    queryClient.clear();
                    void logout();
                  }
                }}
                key={index}
              >
                <NavbarPopoverBase.Item last={isLast}>
                  <Icon />
                  <NavbarPopoverBase.ItemText
                    variant={item.action === "logout" ? "danger" : "default"}
                  >
                    {item.text}
                  </NavbarPopoverBase.ItemText>
                </NavbarPopoverBase.Item>
                {isLast ? null : (
                  <Separator
                    borderBottomWidth={"$0.2"}
                    width={"100%"}
                    borderColor={"$gray-100"}
                  />
                )}
              </NavbarPopoverBase.Close>
            );
          })}
        </NavbarPopoverBase.Content>
      </NavbarPopoverBase.Body>
    </>
  );
};

type AccordionContent =
  | {
      title: string;
      domain: string;
    }
  | {
      title: string;
      slice: string;
    };

interface SideBarAccordionProps {
  id: string;
  title: string;
  content: AccordionContent[];
}

const SizeBarMenu = () => {
  const person = useMe();
  const [bagReportOpen, setBagReportOpen] = useState(false);

  const [task] = api.home.tasksStatus.useSuspenseQuery(undefined, {
    refetchOnMount: true,
  });

  const [data] = api.slices.getAllLatestResults.useSuspenseQuery();

  const hasSlices = task.todo.hasHooksResults;
  const slicesAccordion = useMemo(() => {
    return {
      title: "Додаткові показники",
      content: data.map((item) => {
        return {
          title: item.title,
          slice: item.type,
        };
      }),
    };
  }, [data]);

  const { data: spheres } = api.scores.spheres.useQuery({});

  const { match } = usePathIncludes();

  const navbarItems = useMemo(() => {
    return spheres?.data
      .map((item) => ({ title: item.title, domain: item.domain }))
      .filter((item) => item.domain === "hmt");
  }, [spheres]);

  return (
    <>
      <BagReportForm
        open={bagReportOpen}
        setOpen={setBagReportOpen}
        closeDialog={() => setBagReportOpen(false)}
      />
      <SideBar.Provider>
        <SideBar.Trigger data-id={"sidebar-trigger"}>
          <BaseHeader.HamburgerButton />
        </SideBar.Trigger>
        <SideBar.Portal>
          <SideBar.Overlay
            backgroundColor={"$gray-primary-900"}
            opacity={0.6}
          />
          <SideBar.Content portion="3/4">
            <View
              width={"100%"}
              data-id={"sidebar-body"}
              backgroundColor={"white"}
              height={"100%"}
            >
              <View
                padding={"$4"}
                alignItems="flex-end"
                backgroundColor={"$green-500"}
                width={"100%"}
                gap={"$2"}
              >
                <SideBar.Close
                  data-id={"sidebar-close"}
                  alignItems="center"
                  flexDirection="row"
                  gap={"$0.5"}
                >
                  <Text
                    fontFamily={"$subtitle"}
                    fontWeight={"$4"}
                    fontSize={"$2"}
                    lineHeight={"$2"}
                    color={"$gray-secondary-400"}
                  >
                    Закрити меню
                  </Text>
                  <X />
                </SideBar.Close>
                <Separator
                  maxWidth={170}
                  width={"100%"}
                  borderBottomWidth={"$0.2"}
                  borderBottomColor={"$gray-600"}
                />

                <Link
                  href={"/profile"}
                  data-id={"sidebar-profile-link"}
                  asChild
                >
                  <Text
                    width={"100%"}
                    fontFamily={"$subtitle"}
                    fontWeight={"$5"}
                    fontSize={"$2"}
                    lineHeight={"$2"}
                    color={"white"}
                    textAlign="right"
                  >
                    {person.name}
                  </Text>
                </Link>
              </View>

              <View paddingRight={"$4"} gap={"$2"} flex={1} paddingTop={"$3"}>
                <SideBarAccordion
                  defaultOpen={
                    [
                      match("results") ? "results" : undefined,
                      match("slices") ? "slices" : undefined,
                    ].filter(Boolean) as string[]
                  }
                  data={
                    [
                      navbarItems
                        ? {
                            id: "results",
                            title: "Мої результати",
                            content: navbarItems,
                          }
                        : undefined,
                      hasSlices
                        ? {
                            id: "slices",
                            ...slicesAccordion,
                          }
                        : undefined,
                    ].filter(Boolean) as SideBarAccordionProps[]
                  }
                >
                  {(content) => (
                    <View paddingVertical={"$2"}>
                      {content?.map((item) => {
                        if ("domain" in item) {
                          return (
                            <Link
                              data-id={"sidebar-result-link-" + item.domain}
                              key={item.domain}
                              href={"/results/" + item.domain}
                              style={{
                                cursor: "pointer",
                                alignSelf: "flex-end",
                              }}
                            >
                              <View height="$4">
                                <SideBarAccordionText
                                  selected={match(item.domain)}
                                >
                                  {item.title}
                                </SideBarAccordionText>
                              </View>
                            </Link>
                          );
                        }
                        return (
                          <Link
                            key={item.slice}
                            data-id={"sidebar-slice-link-" + item.slice}
                            href={"/slices/" + item.slice}
                            style={{
                              cursor: "pointer",
                              alignSelf: "flex-end",
                            }}
                          >
                            <View height="$4">
                              <SideBarAccordionText
                                selected={match(item.slice)}
                              >
                                {item.title}
                              </SideBarAccordionText>
                            </View>
                          </Link>
                        );
                      })}
                    </View>
                  )}
                </SideBarAccordion>
              </View>

              <View
                paddingHorizontal={"$4"}
                maxHeight={170}
                flex={1}
                alignItems="flex-end"
                width={"100%"}
                gap={"$3"}
              >
                <Button
                  onPress={() => setBagReportOpen(true)}
                  variant="transparent"
                  padding={0}
                >
                  <XStack alignItems="center" gap={"$0.5"}>
                    <WarningSign />
                    <Text
                      fontFamily={"$subtitle"}
                      fontWeight={"$4"}
                      fontSize={"$2"}
                      lineHeight={"$2"}
                      color={"$gray-primary-900"}
                    >
                      Помітили помилку?
                    </Text>
                  </XStack>
                </Button>

                <Separator
                  borderBottomWidth={"$0.2"}
                  width={"100%"}
                  maxWidth={170}
                  borderColor={"$gray-100"}
                />

                <Button onPress={logout} variant="transparent" padding={0}>
                  <XStack alignItems="center" gap={"$0.5"}>
                    <Exit />
                    <Text
                      fontFamily={"$subtitle"}
                      fontWeight={"$4"}
                      fontSize={"$2"}
                      lineHeight={"$2"}
                      color={"$error-500"}
                    >
                      Вихід
                    </Text>
                  </XStack>
                </Button>
              </View>
            </View>
          </SideBar.Content>
        </SideBar.Portal>
      </SideBar.Provider>
    </>
  );
};

interface AccordionData<T> {
  id: string;
  title: string;
  content: T;
}

// TODO: should be moved to uikit

const SideBarAccordionText = styled(Text, {
  name: "SideBarAccordionText",
  fontFamily: "$body",
  fontSize: "$2",
  lineHeight: "$2",

  variants: {
    selected: {
      true: {
        fontWeight: "$6",
        color: "$pinkmain-500",
      },
      false: {
        fontWeight: "$4",
        color: "$gray-primary-900",
      },
    },
  } as const,
});

function SideBarAccordion<T>({
  data,
  children,
  defaultOpen = [],
}: {
  data: AccordionData<T>[];
  children: (content: AccordionData<T>["content"]) => ReactNode;
  defaultOpen?: string[];
}) {
  const [open, setOpen] = useState<string[]>(defaultOpen);
  return (
    <Accordion
      data-id={"sidebar-accordion"}
      onValueChange={(value) => {
        setOpen((prev) => {
          if (value.length === 0) {
            return [];
          }
          if (prev.some((item) => value.includes(item))) {
            return value.filter((val) => !prev.includes(val));
          }
          return [...prev, ...value];
        });
      }}
      value={open}
      overflow="hidden"
      gap={"$2"}
      type="multiple"
      width={"auto"}
    >
      {data.map((item) => {
        return (
          <Accordion.Item
            data-id={"sidebar-accordion-item"}
            alignSelf="flex-end"
            borderWidth={0}
            key={item.id}
            value={item.id}
          >
            <Accordion.Trigger
              data-id={"sidebar-accordion-trigger"}
              alignSelf="flex-end"
              borderBottomWidth={"0.2"}
              borderBottomColor={"$gray-800"}
              height={"$4"}
              unstyled
              padding={0}
              justifyContent="center"
              alignItems="center"
              width={"auto"}
              flexGrow={0}
              gap={"$0.5"}
              flexDirection="row"
            >
              {({ open }: { open: boolean }) => (
                <>
                  <Text
                    fontFamily={"$body"}
                    fontWeight={"$6"}
                    fontSize={"$3"}
                    lineHeight={"$3"}
                  >
                    {item.title}
                  </Text>

                  <Square rotate={open ? "180deg" : "0deg"}>
                    <ChevronDown color={"$gray-primary-900"} size="$2" />
                  </Square>
                </>
              )}
            </Accordion.Trigger>

            <Accordion.Content
              data-id={"sidebar-accordion-content"}
              padding={0}
            >
              {children(item.content)}
            </Accordion.Content>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}
